import React from 'react';

import { useModal } from '@/components/hooks/useModal';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Text } from '@/components/shared/element/text';
import { HeadingItem } from '@/components/shared/headline/heading-item';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { generateTabletAndMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { IButton } from '@/types/shared';

export interface IServiceIntro {
  dataTestSection?: string;
  dataTest?: string;
  dataTrack?: string;
  theme?: 'green' | 'blue';
  bgColor?: 'dark' | 'gray' | 'light';
  youtubeId?: string;
  lead?: string;
  title?: string;
  description?: string;
  features?: string[];
  buttons?: IButton[];
  image?: ImageProps;
  remark?: string;
  logo?: ImageProps;
  logoFakePart?: ImageProps;
  isReverse?: boolean;
}

const ServiceIntroSection: React.FC<IServiceIntro> = ({
  bgColor = 'light',
  dataTestSection = 'section_intro',
  dataTest = 'intro',
  dataTrack = 'service-intro',
  ...props
}) => {
  const { api: modal, component: modalComponent } = useModal({
    type: 'video',
  });

  const onOpenModal = (youtubeID: string) => {
    modal.open({ youtubeID });
  };

  const bgVariant = () => {
    switch (bgColor) {
      case 'dark':
        return 'gradient-gray-dark';
      case 'gray':
        return 'gray-light';
      default:
        return 'white';
    }
  };

  return (
    <>
      <Section size="2xl" backgroundColor={bgVariant()} test={dataTestSection}>
        <Container>
          <Grid
            templateColumns={{ '@lg': '12' }}
            autoFlow={{ '@lg': props.isReverse ? 'dense' : 'column-dense' }}
            alignItems={{ '@lg': 'center' }}
            columnGap={{ '@lg': '6' }}
            rowGap="10"
          >
            <Box
              css={{
                '@lg': {
                  gridColumn: props.isReverse ? '1 / span 6' : '7 / span 6',
                },
                '@xl': {
                  gridColumn: props.isReverse ? '2 / span 5' : '7 / span 5',
                },
              }}
            >
              {!!props?.image?.src && (
                <Box position="relative" data-hover>
                  <Image
                    priority={props.image.priority}
                    hoverEffect={props.youtubeId ? 'zoom' : undefined}
                    src={props.image.src}
                    alt={props.image.alt}
                    width={600}
                    height={450}
                    layout="responsive"
                    aspectRatio="4by3"
                    sources={[
                      {
                        srcset: props.image.src,
                        media: { minWidth: 1024 },
                        options: { width: 600, crop: '4:3' },
                      },
                      ...generateTabletAndMobileImageSources({
                        mobileSrc: props.image.src,
                        mobileOptions: {
                          crop: '4:3',
                        },
                      }),
                    ]}
                  />
                  {!!props.youtubeId && (
                    <>
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        css={{ position: 'absolute', inset: 0, zIndex: '1' }}
                      >
                        <ButtonIcon
                          icon="play"
                          size="lg"
                          variant={
                            props.theme === 'blue'
                              ? 'button-icon-color-white-icon-blue'
                              : 'button-icon-color-white'
                          }
                          shadow
                          onClick={() => onOpenModal(props.youtubeId as string)}
                          tracking={{
                            dataTrack: dataTrack,
                            dataTrackText: 'youtube',
                            dataTrackUrl:
                              'https://www.youtube.com/watch?v=' +
                              props.youtubeId,
                          }}
                          test={{
                            dataTest: `${dataTest}_button_playVideo`,
                          }}
                        />
                      </Flex>
                      <Box
                        position="absolute"
                        inset="0"
                        zIndex="1"
                        cursor="pointer"
                        className="intro-link"
                        onClick={() => onOpenModal(props.youtubeId as string)}
                        tracking={{
                          dataTrack: dataTrack,
                          dataTrackText: 'youtube',
                          dataTrackUrl:
                            'https://www.youtube.com/watch?v=' +
                            props.youtubeId,
                        }}
                        test={{
                          dataTest: `${dataTest}_button_playVideo`,
                        }}
                      >
                        <Text variant="text-white" font="bold" sr-only>
                          Play Video
                        </Text>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
            <Grid
              rowGap="6"
              css={{
                '@lg': {
                  gridColumn: props.isReverse ? '7 / span 6' : '1 / span 6',
                  maxWidth: 'calc(100% - 56px)',
                  marginLeft: props.isReverse ? 'auto' : '$space-0',
                },
                '@xl': {
                  gridColumn: props.isReverse ? '7 / span 5' : '2 / span 5',
                },
              }}
            >
              <HeadingItem
                lead={props.lead}
                title={props.title as string}
                description={props.description}
                features={props.features}
                logo={props.logo}
                logoFakePart={props.logoFakePart}
                remark={props.remark}
                buttons={props.buttons}
                isDarkTheme={bgColor === 'dark' ? true : false}
                theme={props.theme}
              />
            </Grid>
          </Grid>
        </Container>
      </Section>
      {modalComponent}
    </>
  );
};
ServiceIntroSection.displayName = 'ServiceIntroSection';
export default ServiceIntroSection;
